import { type ReactElement, useMemo } from 'react'
import { DataTable } from 'mantine-datatable'
import { type ApiKeyData, isEmpty } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { modals } from '@mantine/modals'
import { Group } from '@mantine/core'
import { useApiKeys, useTranslation, translateTitles } from '@front/model'
import { NotReady, RelativeDateWithTooltip, ButtonAdd, Page, ScopeProtectedButton, UserCell } from '@components'
import { useUserAllowedPermissionTypes } from './useUserAllowedPermissionTypes'
import { UpdateKey } from './KeyUpdateModal'
import { KeyCreationModal } from './KeyCreationModal'
import { DeleteKey } from './DeleteKey'

const COLUMNS_BASE = [
  { accessor: 'description', title: 'Description' },
  {
    accessor: 'ownerId',
    title: 'Owner',
    render: ({ ownerId }: ApiKeyData) => <UserCell ownerId={ownerId} />,
  },
  {
    accessor: 'expiresAt',
    title: 'Expires',
    render: ({ expiresAt }: ApiKeyData) => {
      return <RelativeDateWithTooltip date={expiresAt} />
    },
  },
  {
    accessor: 'createdAt',
    title: 'Created',
    render: ({ createdAt }: ApiKeyData) => {
      if (!createdAt) return
      return <RelativeDateWithTooltip calendar date={createdAt} />
    },
  },
  {
    accessor: 'lastAccess',
    title: 'Last Access',
    render: ({ lastAccess }: ApiKeyData) => {
      if (!lastAccess) return null
      return <RelativeDateWithTooltip calendar date={lastAccess} />
    },
  },
  {
    accessor: 'allowedPermissions',
    title: 'Allowed Permissions',
    render: ({ allowedPermissions }: ApiKeyData) => {
      if (isEmpty(allowedPermissions)) return 'All'
      return allowedPermissions.length
    },
  },
  {
    accessor: 'actions',
    title: 'Actions',
    render: (k: ApiKeyData) => (
      <Group>
        <UpdateKey {...k} />
        <DeleteKey {...k} />
      </Group>
    ),
  },
]

function ApiKeys(): ReactElement {
  const { t } = useTranslation()
  const { data, isLoading, error, isFetching, refetch } = useApiKeys()
  const availablePermissions = useUserAllowedPermissionTypes()

  const columns = useMemo(() => translateTitles(COLUMNS_BASE, t), [t])

  if (isLoading || error || !data) return <NotReady data={data} error={error} id='API keys' isLoading={isLoading} />

  const handleAddButton = (): void => {
    modals.open({
      title: t('Add API Key'),
      size: '52rem',
      children: <KeyCreationModal availablePermissions={availablePermissions} />,
    })
  }

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title={t('API Keys')}>
        <ScopeProtectedButton criticalPath='/apikey/add'>
          <ButtonAdd onClick={handleAddButton}>{t('Add API Key')}</ButtonAdd>
        </ScopeProtectedButton>
      </Page.Heading>
      <Page.Content>
        <DataTable idAccessor='createdAt' records={data} style={{ minHeight: '200px' }} columns={columns} />
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/integration/apikey/')({
  component: ApiKeys,
})
